import { useState, useRef, useEffect } from "react";
import { fetchDbQuestions } from "../services/chatbotService";
import {
  extractQuestionsFromMessage,
  getMergedCustomStyle,
  convertNonDbQuestion,
  normalizeBlanks,
} from "../services/chatbotUtils";

export const useChatbotQuestions = ({
  messages,
  activeSessionId,
  loading,
  sendChatMessage,
  passCash,
  model,
  difficulty,
  isAnExam,
  t,
}) => {
  const [activeQuestions, setActiveQuestions] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [examAnswers, setExamAnswers] = useState({});
  const [solvedQuestions, setSolvedQuestions] = useState({});
  const [showedHints, setShowedHints] = useState({});
  const [sessionStartTime, setSessionStartTime] = useState(0);
  const [showFinalReportCard, setShowFinalReportCard] = useState(false);
  const [finalEvaluationHtml, setFinalEvaluationHtml] = useState(null);
  const [isWaitingForEvaluation, setIsWaitingForEvaluation] = useState(false);
  const [questionOverlay, setQuestionOverlay] = useState({
    show: false,
    type: "success",
    text: "",
  });
  const [questionMetrics, setQuestionMetrics] = useState({});

  const lastProcessedQuestionsJson = useRef(null);
  const overlayTimeoutRef = useRef(null);

  const resetQuestionsState = () => {
    lastProcessedQuestionsJson.current = null;
    setActiveQuestions(null);
    setCurrentQuestionIndex(0);
    setExamAnswers({});
    setSolvedQuestions({});
    setShowedHints({});
    setSessionStartTime(0);
    setShowFinalReportCard(false);
    setFinalEvaluationHtml(null);
    setIsWaitingForEvaluation(false);
    setQuestionOverlay({ show: false, type: "success", text: "" });
    setQuestionMetrics({});
  };

  const saveExamAnswer = (id, typeId, answers, results, userMark) => {
    setExamAnswers((prev) => ({
      ...prev,
      [id]: answers,
    }));
    setSolvedQuestions((prev) => ({
      ...prev,
      [id]: {
        typeId,
        results,
        userMark: parseFloat(userMark) || 0,
        solvedAt: Date.now(),
      },
    }));
  };

  const isAnswerComplete = (q, answers) => {
    if (!answers) return false;
    if (Array.isArray(answers)) {
      if (answers.length === 0) return false;
      if (q.Type && (q.Type.Id === 1 || q.Type.Id === 7)) {
        return answers.some((a) => a.isChecked);
      }
      if (q.Type && (q.Type.Id === 4 || q.Type.Id === 34)) {
        return answers.every((a) => a.Answer && a.Answer.trim() !== "");
      }
      return true;
    }
    if (typeof answers === "string") return answers.trim() !== "";
    return true;
  };

  const transitionQuestion = (nextIndex) => {
    if (!activeQuestions) return;
    const currentQ = activeQuestions[currentQuestionIndex];
    const nextQ = activeQuestions[nextIndex];
    if (!currentQ) return;

    const now = Date.now();
    setQuestionMetrics((prev) => {
      const updated = { ...prev };
      if (updated[currentQ.Id]) {
        const elapsed = now - updated[currentQ.Id].lastStart;
        updated[currentQ.Id].timeSpent += elapsed;
        updated[currentQ.Id].lastStart = 0;

        if (isAnExam) {
          const hasAnswer = isAnswerComplete(currentQ, examAnswers[currentQ.Id]);
          updated[currentQ.Id].tries = hasAnswer ? 1 : 0;
        }
      }
      if (nextQ && updated[nextQ.Id]) {
        updated[nextQ.Id].lastStart = now;
      }
      return updated;
    });

    setCurrentQuestionIndex(nextIndex);
  };

  const evaluateCurrentQuestionAndNavigate = () => {
    if (!activeQuestions) return;

    const currentQuestion = activeQuestions[currentQuestionIndex];
    if (!currentQuestion) return;

    if (isAnExam) {
      handleFinishAttempt();
      return;
    }

    const answers = examAnswers[currentQuestion.Id];
    const complete = isAnswerComplete(currentQuestion, answers);
    if (!complete) {
      setQuestionOverlay({
        show: true,
        type: "incomplete",
        text: t
          ? t("Pages.Chatbot.IncompleteAnswer")
          : "الرجاء إدخال الإجابة أولاً قبل الانتقال للخطوة التالية!",
      });
      return;
    }

    // Increment tries
    setQuestionMetrics((prev) => {
      const updated = { ...prev };
      if (updated[currentQuestion.Id]) {
        updated[currentQuestion.Id].tries += 1;
      }
      return updated;
    });

    const solved = solvedQuestions[currentQuestion.Id];
    const userMark = solved ? solved.userMark : 0;
    const isCorrect = parseFloat(userMark) >= (currentQuestion.Mark || 2);

    if (isCorrect) {
      const advice =
        currentQuestion.wordAndAdvice ||
        currentQuestion.WordAndAdvice ||
        (t ? t("Pages.Chatbot.CorrectAnswer") : "تم حل السؤال بنجاح!");
      setQuestionOverlay({ show: true, type: "success", text: advice });
      if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = setTimeout(() => {
        closeQuestionOverlay(true);
      }, 5000);
    } else {
      const hint =
        currentQuestion.hint ||
        currentQuestion.Hint ||
        (t ? t("Pages.Chatbot.WrongAnswer") : "إجابة غير صحيحة، حاول مجدداً!");
      setQuestionOverlay({ show: true, type: "failure", text: hint });
      if (overlayTimeoutRef.current) clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = setTimeout(() => {
        closeQuestionOverlay(false);
      }, 6000);
    }
  };

  const closeQuestionOverlay = (shouldAdvance = false) => {
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }

    setQuestionOverlay((prev) => {
      const overlayType = prev.type;
      setTimeout(() => {
        if (overlayType === "success" && shouldAdvance) {
          if (currentQuestionIndex < activeQuestions.length - 1) {
            transitionQuestion(currentQuestionIndex + 1);
          } else {
            // trigger trophy modal in parent
          }
        }
      }, 0);
      return { show: false, type: "success", text: "" };
    });
  };

  const handleNextQuestion = (onShowTrophy) => {
    if (isAnExam) {
      if (currentQuestionIndex < activeQuestions.length - 1) {
        transitionQuestion(currentQuestionIndex + 1);
      } else {
        if (onShowTrophy) onShowTrophy();
      }
    } else {
      evaluateCurrentQuestionAndNavigate();
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      transitionQuestion(currentQuestionIndex - 1);
    }
  };

  const getAnswerText = (q, answers) => {
    if (!answers) return t ? t("Pages.Chatbot.NotAnswered") : "لم يتم الإجابة";
    if (typeof answers === "string") return answers;
    if (Array.isArray(answers)) {
      if (q.Type && (q.Type.Id === 1 || q.Type.Id === 7)) {
        return answers
          .filter((a) => a.isChecked)
          .map((a) => a.Name || a.text || "")
          .filter(Boolean)
          .join("، ");
      }
      if (q.Type && (q.Type.Id === 4 || q.Type.Id === 34)) {
        return answers
          .map((a) => a.Answer || "")
          .filter(Boolean)
          .join("، ");
      }
      return answers
        .map((a) => {
          if (typeof a === "string") return a;
          if (typeof a === "object" && a !== null) {
            return a.Answer || a.Name || a.text || a.userAnswer || "";
          }
          return "";
        })
        .filter(Boolean)
        .join("، ");
    }
    if (typeof answers === "object" && answers !== null) {
      return (
        answers.Answer ||
        answers.Name ||
        answers.text ||
        JSON.stringify(answers)
      );
    }
    return String(answers);
  };

  const handleFinishAttempt = () => {
    if (!activeQuestions) return;

    const unansweredCount =
      activeQuestions.length - Object.keys(solvedQuestions).length;
    if (unansweredCount > 0) {
      const confirmMsg = t
        ? t("Pages.Chatbot.UnansweredQuestionsConfirm", { count: unansweredCount })
        : `لديك ${unansweredCount} أسئلة لم تقم بحلها بعد. هل أنت متأكد من إنهاء المحاولة؟`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    const now = Date.now();
    const updatedMetrics = { ...questionMetrics };
    const currentQ = activeQuestions[currentQuestionIndex];
    if (
      currentQ &&
      updatedMetrics[currentQ.Id] &&
      updatedMetrics[currentQ.Id].lastStart > 0
    ) {
      const elapsed = now - updatedMetrics[currentQ.Id].lastStart;
      updatedMetrics[currentQ.Id].timeSpent += elapsed;
      updatedMetrics[currentQ.Id].lastStart = 0;
      if (isAnExam) {
        const hasAnswer = isAnswerComplete(currentQ, examAnswers[currentQ.Id]);
        updatedMetrics[currentQ.Id].tries = hasAnswer ? 1 : 0;
      }
    }

    setIsWaitingForEvaluation(true);
    setShowFinalReportCard(true);
    setFinalEvaluationHtml("");
    setQuestionMetrics(updatedMetrics);

    const totalQuestions = activeQuestions.length;
    const solvedCount = Object.keys(solvedQuestions).length;
    const totalMarks = activeQuestions.reduce(
      (acc, q) => acc + (q.Mark || 2),
      0
    );
    const achievedMarks = Object.values(solvedQuestions).reduce(
      (acc, sq) => acc + sq.userMark,
      0
    );
    const timeElapsedSeconds = Math.round((Date.now() - sessionStartTime) / 1000);
    const minutes = Math.floor(timeElapsedSeconds / 60);
    const seconds = timeElapsedSeconds % 60;

    const details = activeQuestions
      .map((q, idx) => {
        const solved = solvedQuestions[q.Id];
        const statusText = solved
          ? solved.userMark >= q.Mark
            ? t ? t("Pages.Chatbot.StatusCorrect") : "صحيحة"
            : solved.userMark > 0
              ? t ? t("Pages.Chatbot.StatusPartial") : "جزئية"
              : t ? t("Pages.Chatbot.StatusIncorrect") : "خاطئة"
          : t ? t("Pages.Chatbot.StatusUnsolved") : "لم يتم حلها";

        const cleanName = q.Name ? q.Name.replace(/<[^>]*>/g, "").trim() : "";
        const answerText = getAnswerText(q, examAnswers[q.Id]);
        const cleanAnswerText = answerText
          ? answerText.replace(/<[^>]*>/g, "").trim()
          : t
            ? t("Pages.Chatbot.NotAnswered")
            : "لم يتم الإجابة";

        const tries = updatedMetrics[q.Id]?.tries || 0;
        const timeSpentSeconds = Math.round(
          (updatedMetrics[q.Id]?.timeSpent || 0) / 1000
        );
        const showedHint = showedHints[q.Id]
          ? t ? t("Pages.Chatbot.Yes") : "نعم"
          : t ? t("Pages.Chatbot.No") : "لا";

        if (t) {
          return t("Pages.Chatbot.EvaluationPromptDetails", {
            idx: idx + 1,
            cleanName,
            cleanAnswerText,
            status: statusText,
            mark: q.Mark || 2,
            userMark: solved ? solved.userMark : 0,
            tries,
            timeSpentSeconds,
            showedHint,
          });
        }

        return `- السؤال ${idx + 1
          }: ${cleanName} | إجابة الطالب: ${cleanAnswerText} | النتيجة: ${statusText} | العلامة الكاملة: ${q.Mark || 2
          } | علامة الطالب: ${solved ? solved.userMark : 0
          } | عدد المحاولات: ${tries} | الوقت المستغرق: ${timeSpentSeconds} ثانية | تم عرض التلميح: ${showedHint}`;
      })
      .join("\n");

    let evaluationPrompt = "";
    if (t) {
      evaluationPrompt =
        t("Pages.Chatbot.EvaluationPromptHeader", {
          totalQuestions,
          solvedCount,
          totalMarks,
          achievedMarks,
          minutes,
          seconds,
        }) +
        "\n" +
        details +
        "\n" +
        t("Pages.Chatbot.EvaluationPromptFooter");
    } else {
      evaluationPrompt = `لقد أكملت حل الأسئلة التفاعلية للدرس. إليك تفاصيل محاولتي للتقييم التربوي:
- عدد الأسئلة: ${totalQuestions}
- الأسئلة المجابة: ${solvedCount}
- العلامة الكلية للدرس: ${totalMarks}
- علامتي الإجمالية: ${achievedMarks}
- الوقت المستغرق في الحل والاستيعاب: ${minutes} دقيقة و ${seconds} ثانية (الوقت المقدر النموذجي للدرس والحل هو حوالي 5-10 دقائق).
تفاصيل الإجابات لكل سؤال:
${details}
يرجى تزويدي بالتقييم التربوي والتعليمي المفصل لمستوى فهمي، وتوضيح الأخطاء إن وجدت بأسلوب تعليمي شيق ومشجع، مع اقتراح خطوات عملية للتحسين وتنسيق التقييم في جدول HTML جذاب وخلاصة سريعة.`;
    }

    sendChatMessage(evaluationPrompt, passCash, model, difficulty, true);
  };

  const handleResetEvaluationAndSolveAgain = () => {
    const initialMetrics = {};
    if (activeQuestions) {
      activeQuestions.forEach((q, idx) => {
        initialMetrics[q.Id] = {
          tries: 0,
          timeSpent: 0,
          lastStart: idx === 0 ? Date.now() : 0,
        };
      });
    }

    setCurrentQuestionIndex(0);
    setExamAnswers({});
    setSolvedQuestions({});
    setShowedHints({});
    setSessionStartTime(Date.now());
    setShowFinalReportCard(false);
    setFinalEvaluationHtml(null);
    setIsWaitingForEvaluation(false);
    setQuestionMetrics(initialMetrics);
  };

  const handleExitSession = () => {
    const confirmMsg = t
      ? t("Pages.Chatbot.ExitSessionConfirm")
      : "هل تريد الخروج من جلسة الأسئلة الحالية والعودة للمحادثة؟";
    if (window.confirm(confirmMsg)) {
      setActiveQuestions(null);
      setCurrentQuestionIndex(0);
      setExamAnswers({});
      setSolvedQuestions({});
      setShowedHints({});
      setShowFinalReportCard(false);
      setFinalEvaluationHtml(null);
      setIsWaitingForEvaluation(false);
    }
  };

  const onShowHint = (qId) => {
    setShowedHints((prev) => ({
      ...prev,
      [qId]: true,
    }));
  };

  const checkForQuestions = async (
    originalBotMsg,
    FontSizeObj,
    activeTab,
    setParentActiveTab,
    setParentMobileActiveTab,
    setIsSwitchingHistory
  ) => {
    if (!originalBotMsg) {
      if (setIsSwitchingHistory) setIsSwitchingHistory(false);
      return;
    }

    try {

      let questions = null;
      if (
        originalBotMsg.text &&
        originalBotMsg.text.includes("lesson-questions-data")
      ) {
        questions = extractQuestionsFromMessage(originalBotMsg.text);
      }

      if (
        !questions &&
        originalBotMsg.questions
      ) {
        questions = originalBotMsg.questions;
      }

      if (questions && questions.length > 0) {
        questions = questions.map((q, idx) => ({
          ...q,
          Id: q.Id || q.id ? String(q.Id || q.id) : `gen_${idx}`,
          isFromDB:
            q.isFromDB === true ||
            q.isFromDB === "true" ||
            q.IsDbQuestion === true ||
            q.IsDbQuestion === "true",
          lessonId: q.lessonId || q.LessonId,
        }));

        const questionsJson = JSON.stringify(questions);
        if (lastProcessedQuestionsJson.current === questionsJson) {
          return;
        }
        lastProcessedQuestionsJson.current = questionsJson;

        let lessonId = null;
        const found = questions.find((q) => q.lessonId || q.LessonId);
        if (found) {
          lessonId = found.lessonId || found.LessonId;
        }

        const getTypeString = (typeVal) => {
          if (!typeVal) return "";
          if (typeof typeVal === "object") {
            return String(typeVal.Name || typeVal.name || typeVal.Id || typeVal.id || "");
          }
          return String(typeVal);
        };

        const getCorrectAnswers = (ca) => {
          const arr = Array.isArray(ca) ? ca : (ca !== undefined && ca !== null ? [ca] : []);
          return arr.map(val => parseInt(val, 10)).filter(val => !isNaN(val));
        };

        const getCustomStyleObject = (cs) => {
          if (!cs) return {};
          if (typeof cs === "string") {
            try {
              const parsed = JSON.parse(cs);
              return typeof parsed === "object" && parsed !== null ? parsed : {};
            } catch (e) {
              return {};
            }
          }
          return typeof cs === "object" ? cs : {};
        };

        const allQuestionItems = questions.map((q) => {
          const isDb = q.isFromDB === true || q.isFromDB === "true";
          if (isDb) {
            const qId = Number(q.Id || q.id);
            return {
              IsFromDB: true,
              QuestionId: isNaN(qId) ? null : qId,
              LessonId: q.lessonId || q.LessonId || lessonId,
              Id: String(q.Id || q.id),
              hint: q.hint || null,
              wordAndAdvice: q.wordAndAdvice || null,
            };
          } else {
            return {
              IsFromDB: false,
              Id: String(q.Id || q.id),
              LessonId: q.lessonId || q.LessonId || lessonId,
              Type: getTypeString(q.type || q.Type),
              QuestionText: normalizeBlanks(String(q.questionText || q.QuestionText || q.Name || q.name || q.Title || q.title || "")),
              Options: Array.isArray(q.options || q.Options) ? (q.options || q.Options) : [],
              CorrectAnswers: getCorrectAnswers(q.correctAnswers || q.CorrectAnswers),
              Categories: Array.isArray(q.categories || q.Categories)
                ? (q.categories || q.Categories).map(cat => {
                  if (typeof cat === "string") {
                    return normalizeBlanks(cat);
                  } else if (cat && typeof cat === "object") {
                    const textKey = cat.text !== undefined ? 'text' : (cat.Text !== undefined ? 'Text' : (cat.name !== undefined ? 'name' : (cat.Name !== undefined ? 'Name' : 'text')));
                    const originalText = cat.text || cat.Text || cat.name || cat.Name || "";
                    return {
                      ...cat,
                      [textKey]: normalizeBlanks(originalText)
                    };
                  }
                  return cat;
                })
                : [],
              Answers: Array.isArray(q.answers || q.Answers) ? (q.answers || q.Answers) : [],
              CorrectMapping: Array.isArray(q.correctMapping || q.CorrectMapping) ? (q.correctMapping || q.CorrectMapping) : [],
              CustomStyle: getCustomStyleObject(q.CustomStyle || q.customStyle),
              hint: q.hint || null,
              wordAndAdvice: q.wordAndAdvice || null,
            };
          }
        });

        let mergedQuestions = [];
        try {
          const fetchedQuestions = await fetchDbQuestions(allQuestionItems);
          mergedQuestions = fetchedQuestions.map((q) => ({
            ...q,
            CustomStyle: getMergedCustomStyle(q),
          }));
        } catch (err) {
          console.error("Failed to fetch/convert questions details:", err);
          mergedQuestions = questions.map((q) => {
            if (q.isFromDB) {
              return {
                ...q,
                CustomStyle: getMergedCustomStyle(q),
              };
            } else {
              return convertNonDbQuestion(q);
            }
          });
        }

        const mergedQuestionsJson = JSON.stringify(mergedQuestions);
        const currentQuestionsJson = JSON.stringify(activeQuestions);
        if (mergedQuestionsJson !== currentQuestionsJson) {
          const firstQ = mergedQuestions[0];
          let fontSizeVal = 18;
          let questionSeparatedVal = {
            BorderStyle: "dashed",
            BorderColor: "rgba(68, 68, 68, 1)",
            BorderWidth: 3,
            width: 100,
          };
          if (firstQ && firstQ.CustomStyle) {
            if (firstQ.CustomStyle.FontSize) {
              fontSizeVal = firstQ.CustomStyle.FontSize;
            }
            if (firstQ.CustomStyle.questionSeparated) {
              questionSeparatedVal = firstQ.CustomStyle.questionSeparated;
            }
          }

          FontSizeObj.setFontSize(fontSizeVal);
          FontSizeObj.setQuestionSeparated(questionSeparatedVal);

          const initialMetrics = {};
          mergedQuestions.forEach((q, idx) => {
            initialMetrics[q.Id] = {
              tries: 0,
              timeSpent: 0,
              lastStart: idx === 0 ? Date.now() : 0,
            };
          });

          setActiveQuestions(mergedQuestions);
          setCurrentQuestionIndex(0);
          setExamAnswers({});
          setSolvedQuestions({});
          setShowedHints({});
          setSessionStartTime(Date.now());
          setShowFinalReportCard(false);
          setFinalEvaluationHtml(null);
          setIsWaitingForEvaluation(false);
          setQuestionMetrics(initialMetrics);

          if (activeTab === "questions") {
            if (setParentActiveTab) setParentActiveTab("questions");
            if (setParentMobileActiveTab) setParentMobileActiveTab("questions");
          } else {
            if (setParentActiveTab) setParentActiveTab("lesson");
            if (setParentMobileActiveTab) setParentMobileActiveTab("lesson");
          }
        }
      }
    } finally {
      if (setIsSwitchingHistory) setIsSwitchingHistory(false);
    }
  };

  useEffect(() => {
    return () => {
      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
      }
    };
  }, []);

  return {
    activeQuestions,
    setActiveQuestions,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    examAnswers,
    setExamAnswers,
    solvedQuestions,
    setSolvedQuestions,
    showedHints,
    setShowedHints,
    sessionStartTime,
    setSessionStartTime,
    showFinalReportCard,
    setShowFinalReportCard,
    finalEvaluationHtml,
    setFinalEvaluationHtml,
    isWaitingForEvaluation,
    setIsWaitingForEvaluation,
    questionOverlay,
    setQuestionOverlay,
    questionMetrics,
    setQuestionMetrics,
    resetQuestionsState,
    saveExamAnswer,
    transitionQuestion,
    evaluateCurrentQuestionAndNavigate,
    closeQuestionOverlay,
    handleNextQuestion,
    handlePrevQuestion,
    handleFinishAttempt,
    handleResetEvaluationAndSolveAgain,
    handleExitSession,
    onShowHint,
    checkForQuestions,
  };
};
